import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import { SpeedInsights } from '@vercel/speed-insights/react'
import { Analytics } from '@vercel/analytics/react'
import posthog from 'posthog-js'
import * as Sentry from '@sentry/react'
import PostHogPageView from './PostHogPageView.jsx'

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import ScrollToTop from './ScrollToTop.jsx'
import Homepage from './Homepage.jsx'
import AboutPage from './AboutPage.jsx'
import ScoresPage from './ScoresPage.jsx'
import ExamsPage from './ExamsPage.jsx'
import ExamsSchedulePage from './ExamsSchedulePage.jsx'
import PomodoroPage from './PomodoroPage.jsx'
import ScoreCalculatorPage from './ScoreCalculatorPage.jsx'
import TargetSchoolsPage from './TargetSchoolsPage.jsx'
import DepartmentDetailsPage from './DepartmentDetailsPage.jsx'
import SubjectExamPage from './ExamSubjectYearPage.jsx'
import AiAdvisorPage from './AiAdvisorPage.jsx'

import Terms from './Terms.jsx'
import Cookies from './Cookies.jsx'
import PrivacyPolicy from './PrivacyPolicy.jsx'
import AppPage from './AppPage.jsx'
import AppPrivacyPolicy from './AppPrivacyPolicy.jsx'
import AppSupportPage from './AppSupportPage.jsx'
import { MAX_YEAR, THEMATA_YEAR, SCHEDULE_YEAR } from './constants.js'

const isProdEnv = import.meta.env.VITE_ENVIRONMENT === 'prod'

if (isProdEnv && import.meta.env.VITE_ADSENSE_CLIENT) {
  const client = import.meta.env.VITE_ADSENSE_CLIENT
  const src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`
  const existing = document.querySelector(`script[src="${src}"]`)

  if (!existing) {
    const script = document.createElement('script')
    script.async = true
    script.src = src
    script.crossOrigin = 'anonymous'
    document.head.appendChild(script)
  }
}

if (isProdEnv && import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
    capture_pageview: false, // SPA manual
    capture_pageleave: true,
    autocapture: false,
    disable_session_recording: true,
    // cookieless_mode: 'always',
  })
}

if (isProdEnv && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT, // "prod"
    // Keep free plan clean:
    tracesSampleRate: 0, // no performance tracing
    // Privacy-friendly:
    sendDefaultPii: false,

    // Drop common noisy browser/extension issues
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      'NetworkError when attempting to fetch resource.',
      'Non-Error promise rejection captured',
      'ChunkLoadError',
      'Loading chunk',
    ],
  })
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <PostHogPageView />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Homepage />} />
        <Route path="/vaseis/:year" element={<ScoresPage />} />
        <Route path="/vaseis" element={<Navigate to={`/vaseis/${MAX_YEAR}?ordering=-score`} replace />} />
        <Route path="/themata" element={<Navigate to={`/themata/${THEMATA_YEAR}`} replace />} />
        <Route path="/themata/:year" element={<ExamsPage />} />
        <Route path="/themata/:subjectSlug/:year" element={<SubjectExamPage />} />
        <Route path="/programma" element={<Navigate to={`/programma/${SCHEDULE_YEAR}`} replace />} />
        <Route path="/programma/:year" element={<ExamsSchedulePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/app" element={<AppPage />} />
        {/*
          Η εφαρμογή δηλώνει αυτά τα δύο URL στο App Store Connect και στο Play
          Console. Είναι ΧΩΡΙΣΤΑ από τα /privacy και /terms του ιστότοπου: ο
          ιστότοπος έχει AdSense, PostHog και Sentry, η εφαρμογή δεν συλλέγει
          τίποτα. Αν δοθεί στα stores η πολιτική του ιστότοπου, δηλώνεται
          συλλογή δεδομένων που η εφαρμογή δεν κάνει.
        */}
        <Route path="/app/privacy" element={<AppPrivacyPolicy />} />
        <Route path="/app/support" element={<AppSupportPage />} />
        <Route path="/pomodoro" element={<PomodoroPage />} />
        <Route path="/ypologismos-morion" element={<ScoreCalculatorPage />} />
        <Route path="/ai-symvoulos" element={<AiAdvisorPage />} />
        <Route path="/stoxoi-sxolon" element={<TargetSchoolsPage />} />
        <Route path="/tmima/:slug" element={<DepartmentDetailsPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <SpeedInsights />
      <Analytics />
    </BrowserRouter>
  </StrictMode>
)
